import { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../../Config/axiosInstance";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import "./ClientDeleteDialog.css";

export default function ClientDeleteDialog({
  open,
  onClose,
  client,
  onDeleted,
}) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const rawClient = useMemo(() => client?.__raw ?? client ?? null, [client]);

  useEffect(() => {
    if (!open) return;
    setDeleting(false);
    setError("");
  }, [open]);

  const handleDelete = async () => {
    if (!rawClient?.id) return;

    setError("");
    setDeleting(true);

    try {
      await axiosInstance.delete(`/clients/${rawClient.id}/`);
      onDeleted?.();
      onClose?.();
    } catch (e) {
      setError(
        "Cannot delete this client because it is linked to other records. Please remove or update those dependencies first."
      );
    } finally {
      setDeleting(false);
    }
  };

  const clientName =
    rawClient?.name ||
    rawClient?.client_name ||
    rawClient?.company_name ||
    "Unknown Client";

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      className="client-delete-dialog"
      PaperProps={{
        elevation: 24,
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {!error && (
        <DialogTitle className="cld-header">
          <Box className="cld-header-content">
            <Box className="cld-title-section">
              <WarningAmberIcon className="cld-warning-icon" />
              <Typography variant="h5" component="span" className="cld-title">
                Delete Client
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              className="cld-close-btn"
              size="small"
              disabled={deleting}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
      )}

      <DialogContent className="cld-content">
        {error ? (
          <Alert
            severity="error"
            className="cld-error-alert"
            icon={<ErrorOutlineIcon />}
          >
            <Typography variant="body1" fontWeight={500}>
              Unable to Delete Client
            </Typography>
            <Typography variant="body2" className="cld-error-message">
              {error}
            </Typography>
          </Alert>
        ) : (
          <Box className="cld-confirm-content">
            <Box className="cld-client-container">
              <Typography variant="subtitle2" className="cld-client-label">
                Client
              </Typography>
              <Typography variant="h6" className="cld-client-value">
                {clientName}
              </Typography>
            </Box>

            <Typography variant="body1" className="cld-warning-text">
              This action is permanent and cannot be undone.
            </Typography>

            <Box className="cld-consequences">
              <Typography variant="body2" className="cld-consequence-item">
                • All associated client data may be affected
              </Typography>
              <Typography variant="body2" className="cld-consequence-item">
                • Related contacts and references may no longer be available
              </Typography>
              <Typography variant="body2" className="cld-consequence-item">
                • This change cannot be reversed
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions className="cld-actions">
        {error ? (
          <Button
            onClick={onClose}
            variant="contained"
            className="cld-btn cld-btn-ok"
          >
            Got It
          </Button>
        ) : (
          <>
            <Button
              onClick={onClose}
              disabled={deleting}
              variant="outlined"
              className="cld-btn cld-btn-cancel"
            >
              Cancel
            </Button>

            <Button
              onClick={handleDelete}
              disabled={deleting || !rawClient?.id}
              variant="contained"
              className="cld-btn cld-btn-delete"
              startIcon={
                deleting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <DeleteOutlineIcon />
                )
              }
              autoFocus
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}